import { inject } from "mobx-react";
import { useCallback, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { SearchIcon } from "../../../../core/components/icons/SearchIcon.component";
import { RemoteListChildProps } from "../../../../core/components/remote/RemoteList.component";
import { CoastSearchItemFragment, GetPwsSearchResultsQueryVariables } from "@ihr-radioedit/inferno-webapi";
import { Button, LoadMoreFromCursor, Spinner } from "../../../../core/ui";
import { CheckBoxInput, FORM_ERRORS, SimpleInput } from "../../components/forms";
import { SearchItem } from "./SearchItem.component";
import "./SearchResults.style.scss";

interface CoastSearchFormInputs {
  includeArticles: boolean;
  includeGuests: boolean;
  includeShows: boolean;
  keyword: string;
}

interface SearchResultsProps extends RemoteListChildProps<CoastSearchItemFragment, GetPwsSearchResultsQueryVariables> {
  keyword?: string;
}

interface SearchResultsItemsProps {
  results: CoastSearchItemFragment[];
  allSearchTypesUnchecked: boolean;
}

const SearchResultsItems = ({ results, allSearchTypesUnchecked }: SearchResultsItemsProps) => {
  if (allSearchTypesUnchecked) {
    return <p>Please select at least one type</p>;
  }

  return results.length ? (
    <>
      {results.map(item => (
        <SearchItem item={item} key={item.ref_id} />
      ))}
    </>
  ) : (
    <p>No results for your request</p>
  );
};

export const SearchResults = inject("store")(
  ({ data: results, keyword, loading, refresh, ...remote }: SearchResultsProps) => {
    const [allSearchTypesUnchecked, setAllSearchTypesUnchecked] = useState(false);

    const { errors, register, handleSubmit } = useForm<CoastSearchFormInputs>({
      defaultValues: {
        includeArticles: true,
        includeGuests: true,
        includeShows: true,
        keyword,
      },
    });

    const onSubmit: SubmitHandler<CoastSearchFormInputs> = useCallback(
      (input, e) => {
        e?.preventDefault();
        if (!(input.includeArticles || input.includeGuests || input.includeShows)) {
          setAllSearchTypesUnchecked(true);
        } else {
          if (allSearchTypesUnchecked) {
            setAllSearchTypesUnchecked(false);
          }

          refresh({
            options: { ...input, cursor: undefined, offset: 0 },
            quiet: false,
            useEmptyResults: true,
          });
        }
      },
      [allSearchTypesUnchecked, refresh],
    );

    return (
      <>
        <section className="component-coast-search-results">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="search-query">
              <SimpleInput
                placeholder="Search..."
                rules={{ required: FORM_ERRORS.required }}
                className="search-query-input"
                error={errors.keyword}
                register={register}
                name="keyword"
                type="text"
              />

              <Button className="search-query-submit" disabled={!!errors.keyword || loading} type="submit">
                {loading ? <Spinner visible={true} /> : <SearchIcon />}
              </Button>
            </div>

            <div className="search-types-selectors">
              <CheckBoxInput label="Shows" name="includeShows" register={register} />
              <CheckBoxInput label="Articles" name="includeArticles" register={register} />
              <CheckBoxInput label="Guests" name="includeGuests" register={register} />
            </div>
          </form>

          <div className="search-items">
            <SearchResultsItems allSearchTypesUnchecked={allSearchTypesUnchecked} results={results} />
          </div>
        </section>

        {!allSearchTypesUnchecked ? (
          <LoadMoreFromCursor sectionName="search" hasNext={remote.hasNext} loading={loading} nextFn={remote.next} />
        ) : null}
      </>
    );
  },
);
